import {Component, EventEmitter, forwardRef, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {Organization} from '../../../types-repository';
import {RouterModule} from '@angular/router';
import {s3TagValueLengthValidator} from '../../name-limits';
import {countInvalidFields} from '../form-messages';
import {IfeFormat} from '../../../target_systems';


@Component({
    selector: 'app-basic-poitem-info',
    templateUrl: './basic-poitem-info.component.html',
    styleUrls: ['./basic-poitem-info.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => BasicPoitemInfoComponent),
        },
        {
            provide: NG_VALIDATORS,
            useExisting: BasicPoitemInfoComponent,
            multi: true
        }],
  imports: [
    ReactiveFormsModule,
    RouterModule
]
})
export class BasicPoitemInfoComponent implements OnInit, ControlValueAccessor {
  static DEFAULTS = {
    input_brefix: '',
    po_item_id: '',
    filename: '',
  };
  form: FormGroup;

  /** Set by the PO form once a submit was refused: from then on invalid fields show as such. */
  @Input()
  submitted = false;

  /** Whether a control shows its error: invalid, and touched or after a refused submit. */
  showInvalid(name: string): boolean {
    const c = this.form?.controls[name];
    return !!c && c.invalid && (c.touched || this.submitted);
  }

  @Input()
  organization: Organization;

  @Input()
  ife_format: IfeFormat;

  @Output()
  pasteFilenames = new EventEmitter<string>();

  private changed;
  private touched;

  // Encode ID validation. Most formats restrict it to a single path segment; formats
  // with `allow_path_in_encode_id` additionally permit '/' as a separator (segments
  // only — no leading, trailing, or double slash) so published files can nest.
  private static readonly ENCODE_ID_SEGMENT = '[A-Za-z0-9_\\-. ]+';

  get poItemIdPattern(): string {
    return this.ife_format?.allow_path_in_encode_id
      ? `${BasicPoitemInfoComponent.ENCODE_ID_SEGMENT}(/${BasicPoitemInfoComponent.ENCODE_ID_SEGMENT})*`
      : BasicPoitemInfoComponent.ENCODE_ID_SEGMENT;
  }

  get poItemIdPatternDescription(): string {
    return this.ife_format?.allow_path_in_encode_id
      ? 'This field can only contain letters, numbers, _ - . and / as a path separator (no leading, trailing, or double slash).'
      : 'This field can only contain letters, numbers, _ & -.';
  }

  constructor(private formBuilder: FormBuilder) {
  }


  writeValue(value) {
    this.form.patchValue(value, {emitEvent: false})
  }

  registerOnChange(fn) {
    this.changed = fn;
  }

  registerOnTouched(fn) {
    this.touched = fn;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      input_brefix: ['', [Validators.required]],
      // Publish writes the title, custom_quote-encoded, into an S3 object tag.
      po_item_id: ['', [Validators.required, s3TagValueLengthValidator()]],
      filename: '',
    })
    this.form.valueChanges.subscribe((val) => {
      if (this.touched) {
        this.touched();
      }
      if (this.changed) {
        this.changed(val);
      }
    });
    // ngOnChanges fires before ngOnInit, so if ife_format was already set when this
    // component was created (e.g. user selected format before adding folders), the
    // validator wasn't applied yet because this.form didn't exist. Apply it now.
    this.applyFilenameValidator();
  }

  validate({value}: FormControl) {
    const isNotValid = !this.form.valid
    return isNotValid && {
      invalid: true,
      invalidFields: countInvalidFields(this.form),
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyFilenameValidator();
  }

  private applyFilenameValidator(): void {
    if (this.ife_format && this.form) {
      if (this.ife_format.filename === '') {
        this.form.controls['filename'].clearValidators();
      } else {
        this.form.controls['filename'].setValidators(Validators.required);
      }
      this.form.controls['filename'].updateValueAndValidity();
    }
  }

  onPaste(event: ClipboardEvent) {
    console.log(event);
    const text = event.clipboardData?.getData('text') || '';
    console.log(text);
    if (text) {
      this.pasteFilenames.emit(text);
    }


  }

}
